import React, { useState, useMemo, useEffect } from 'react';
import { HomeIcon, HeartIcon, CalendarDaysIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { HomeIcon as HomeIconSolid } from '@heroicons/react/24/solid';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

interface VisitationStatusSectionProps {
  totalVisitations?: number;
  scheduledVisitations?: number;
  completedVisitations?: number;
  urgentCases?: number;
}

// 색상 상수
const VISITATION_COLORS = ['#DC2626', '#059669', '#1D4ED8', '#7C3AED'];
const STATUS_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
const PRIORITY_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#6B7280'];

export const VisitationStatusSection: React.FC<VisitationStatusSectionProps> = ({
  totalVisitations = 48,
  scheduledVisitations = 12,
  completedVisitations = 36,
  urgentCases = 5
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'status' | 'schedule' | 'trends'>('overview');
  const [animatedValues, setAnimatedValues] = useState({
    totalVisitations: 0,
    scheduledVisitations: 0,
    completedVisitations: 0,
    urgentCases: 0
  });

  // 애니메이션 효과
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        totalVisitations,
        scheduledVisitations,
        completedVisitations,
        urgentCases
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [totalVisitations, scheduledVisitations, completedVisitations, urgentCases]);

  // 심방 현황 데이터
  const visitationData = useMemo(() => [
    {
      name: '완료',
      value: completedVisitations,
      color: '#10B981',
      percentage: totalVisitations > 0 ? ((completedVisitations / totalVisitations) * 100).toFixed(1) : '0',
      description: '완료된 심방'
    },
    {
      name: '예정',
      value: scheduledVisitations,
      color: '#F59E0B',
      percentage: totalVisitations > 0 ? ((scheduledVisitations / totalVisitations) * 100).toFixed(1) : '0',
      description: '예정된 심방'
    },
    {
      name: '긴급',
      value: urgentCases,
      color: '#EF4444',
      percentage: totalVisitations > 0 ? ((urgentCases / totalVisitations) * 100).toFixed(1) : '0',
      description: '긴급 심방 필요'
    }
  ], [totalVisitations, completedVisitations, scheduledVisitations, urgentCases]);

  // 심방 유형 데이터
  const visitationTypeData = useMemo(() => [
    { type: '정기 심방', count: 18, priority: '보통', color: '#10B981' },
    { type: '신규 교인', count: 12, priority: '높음', color: '#3B82F6' },
    { type: '병문안', count: 8, priority: '긴급', color: '#EF4444' },
    { type: '위로 심방', count: 6, priority: '높음', color: '#F59E0B' },
    { type: '축하 심방', count: 4, priority: '보통', color: '#8B5CF6' }
  ], []);

  // 월별 심방 트렌드 데이터
  const trendData = useMemo(() => [
    { month: '1월', 완료: 28, 예정: 8, 긴급: 3 },
    { month: '2월', 완료: 32, 예정: 10, 긴급: 4 },
    { month: '3월', 완료: 30, 예정: 9, 긴급: 2 },
    { month: '4월', 완료: 35, 예정: 11, 긴급: 4 },
    { month: '5월', 완료: 38, 예정: 13, 긴급: 6 },
    { month: '6월', 완료: completedVisitations, 예정: scheduledVisitations, 긴급: urgentCases }
  ], [completedVisitations, scheduledVisitations, urgentCases]);

  // 완료율 계산
  const completionRate = totalVisitations > 0 ? Math.round((completedVisitations / totalVisitations) * 100) : 0;
  
  return (
    <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-red-200 dark:border-red-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl">
            <HomeIconSolid className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">심방관리 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">교인 심방 및 목양 관리 시스템</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            completionRate >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            completionRate >= 60 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-2 inline-block ${
              completionRate >= 80 ? 'bg-green-500' :
              completionRate >= 60 ? 'bg-blue-500' : 'bg-yellow-500'
            } animate-pulse`}></span>
            완료율 {completionRate}%
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-red-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 현황
        </button>
        <button
          onClick={() => setActiveTab('status')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'status'
              ? 'bg-red-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          상태별 현황
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'schedule'
              ? 'bg-red-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          일정 관리
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'trends'
              ? 'bg-red-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          심방 분석
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 핵심 지표 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 심방</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{animatedValues.totalVisitations}건</p>
              </div>
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <HomeIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">예정 심방</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{animatedValues.scheduledVisitations}건</p>
              </div>
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <CalendarDaysIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">긴급 케이스</p>
                <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{animatedValues.urgentCases}건</p>
              </div>
              <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                <HeartIcon className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-3">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">심방 전체 현황</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">상태별 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie 
                        data={visitationData} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={70} 
                        labelLine={false} 
                        label={{ fontSize: 12 }}
                      >
                        {visitationData.map((entry, index) => (
                          <Cell key={`cell-visitation-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value}건`, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">상세 분석</h4>
                  <div className="space-y-3">
                    {visitationData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">
                          {item.value}건 ({item.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-300">
                      <strong>완료율:</strong> {completionRate}%
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      이번 달 심방 진행 상황
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'status' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">심방 유형별 상세 현황</h3>
              <div className="space-y-4">
                {visitationTypeData.map((type, index) => (
                  <div key={type.type} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: type.color }}></div>
                        <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">{type.type}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          type.priority === '긴급' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          type.priority === '높음' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        }`}>
                          {type.priority}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{type.count}건</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-1000" 
                        style={{ 
                          backgroundColor: type.color,
                          width: `${(type.count / visitationTypeData.reduce((sum, item) => sum + item.count, 0)) * 100}%`
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">심방 일정 관리</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-600 dark:text-green-400 mb-1">완료</p>
                  <p className="text-2xl font-bold text-green-800 dark:text-green-200">{completedVisitations}건</p>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">예정</p>
                  <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">{scheduledVisitations}건</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-1">긴급</p>
                  <p className="text-2xl font-bold text-red-800 dark:text-red-200">{urgentCases}건</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">이번 주</p>
                  <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">8건</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">심방 요약</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 평균 심방 시간: 45분</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 주간 심방 목표: 12건</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 진행 상황: 목표 초과 달성</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 긴급 케이스 대응: 24시간 내</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 정기 심방 주기: 3개월</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 종합 평가: 우수</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">심방 분석 및 트렌드</h3>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">6개월 심방 추이</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="완료" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="예정" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="긴급" stroke="#EF4444" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 dark:text-red-400 font-medium">목양 효과</p>
                      <p className="text-xl font-bold text-red-800 dark:text-red-200">우수</p>
                    </div>
                    <HeartIcon className="h-8 w-8 text-red-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">관계 증진</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">+18%</p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">전망</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-200">매우 긍정적</p>
                    </div>
                    <CalendarDaysIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};